import { ApiError } from "@repo/core";

/** Shape of dj-rest-auth's error responses: either a single `detail`
 * message, non-field validation errors, or per-field lists of messages. */
interface DrfErrorBody {
  detail?: string;
  non_field_errors?: string[];
  email?: string[];
  password1?: string[];
  password2?: string[];
  old_password?: string[];
  new_password1?: string[];
  new_password2?: string[];
  password?: string[];
  reason?: string[];
}

const asDrfErrorBody = (data: unknown): DrfErrorBody | undefined =>
  typeof data === "object" && data !== null ? (data as DrfErrorBody) : undefined;

/** Best-effort single human-readable message out of a login/Google-login
 * failure - `{non_field_errors: [...]}` or `{detail: "..."}`. Returns
 * `undefined` when the error isn't a recognizable API error, so callers can
 * fall back to their own generic translated message. */
export const extractErrorMessage = (error: unknown): string | undefined => {
  if (!(error instanceof ApiError)) return undefined;
  const data = asDrfErrorBody(error.data);
  return data?.non_field_errors?.[0] ?? data?.detail;
};

export interface SignupFieldErrors {
  email?: string;
  password1?: string;
  password2?: string;
}

/** Maps a registration error response onto the three signup fields. Folds
 * `non_field_errors` into `password1` when that field has no error of its
 * own - the same trick anp-fe's RegisterForm.vue uses, since non-field
 * validation errors (e.g. backend-side password strength checks) read most
 * naturally next to the password input. */
export const extractSignupFieldErrors = (error: unknown): SignupFieldErrors => {
  const data = error instanceof ApiError ? asDrfErrorBody(error.data) : undefined;
  if (!data) return {};

  return {
    email: data.email?.[0],
    password1: data.password1?.[0] ?? data.non_field_errors?.[0],
    password2: data.password2?.[0],
  };
};

export interface ChangePasswordFieldErrors {
  oldPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

/** Maps a `/rest-auth/password/change/` error response onto the three
 * password fields. `non_field_errors` folds into the new-password field for
 * the same reason it does on signup - backend-side strength checks read most
 * naturally next to the input they reject. Mirrors anp-fe's
 * ChangePasswordForm.vue, which reads the very same keys off the store. */
export const extractChangePasswordFieldErrors = (
  error: unknown
): ChangePasswordFieldErrors => {
  const data = error instanceof ApiError ? asDrfErrorBody(error.data) : undefined;
  if (!data) return {};

  return {
    oldPassword: data.old_password?.[0],
    newPassword: data.new_password1?.[0] ?? data.non_field_errors?.[0],
    confirmPassword: data.new_password2?.[0],
  };
};

export interface DeactivateAccountFieldErrors {
  password?: string;
  reason?: string;
}

/** Maps a `/rest-auth/deactivate-account/` error response onto its two
 * fields. A wrong current password is by far the common case, so unattributed
 * errors land there. */
export const extractDeactivateFieldErrors = (
  error: unknown
): DeactivateAccountFieldErrors => {
  const data = error instanceof ApiError ? asDrfErrorBody(error.data) : undefined;
  if (!data) return {};

  return {
    password: data.password?.[0] ?? data.non_field_errors?.[0],
    reason: data.reason?.[0],
  };
};

/** The password-reset endpoint answers 401 when no account is registered
 * under the submitted address (stock dj-rest-auth answers 200 regardless;
 * ours is deliberately explicit). Callers translate this into their own
 * "no account for that e-mail" copy, since the backend's own 401 body is
 * not user-facing. */
export const isUnknownEmailError = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 401;

/** Everything else a reset request can fail with: a per-field `email` error
 * (malformed address) or a non-field message. `undefined` means "nothing
 * specific" - the caller falls back to its generic translated message. */
export const extractPasswordResetError = (error: unknown): string | undefined => {
  const data = error instanceof ApiError ? asDrfErrorBody(error.data) : undefined;
  return data?.email?.[0] ?? data?.non_field_errors?.[0] ?? data?.detail;
};
